<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import { RouterView, useRoute, useRouter } from 'vue-router'
  import { useCookies } from '@vueuse/integrations/useCookies'
  import LoginScreen from '@/pages/LoginScreen.vue'
  import { useStore } from './utils/store'
  import { resolveApiBaseUrl } from './utils/apiBase'
  import { UIContentWrap } from '@gohighlevel/ghl-ui'
  import NavMenu from './components/NavMenu.vue'
  import { useFavicon, useTitle } from '@vueuse/core'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const router = useRouter()

  const route = useRoute()
  const title = useTitle()
  const favicon = useFavicon()
  const store = useStore()

  const is404 = ref(false)

  onMounted(async () => {
    const cookies = useCookies(['storeInfo', 'token'])

    const storeInfo = cookies.get('storeInfo')
    if (!storeInfo) {
      is404.value = true
      return
    }
    const path = storeInfo.path
    const locationId = storeInfo.locationId
    const storeId = storeInfo.storeId
    const companyId = storeInfo.companyId
    const name = storeInfo.name
    const icon = storeInfo.favicon
    const apiBaseUrl = resolveApiBaseUrl(storeInfo.apiBaseUrl)
    const tokenFromStorage = cookies.get(`token_${storeId}`)
    let userFromStorage = { name: '', email: '' }
    try {
      userFromStorage = JSON.parse(localStorage.getItem(`user_${storeId}`) ?? '{}')
    } catch {
      localStorage.removeItem(`user_${storeId}`)
    }

    if (!storeId || !locationId) {
      is404.value = true
      return
    }

    store.locationId.value = locationId
    store.storeId.value = storeId
    store.companyId.value = companyId ?? ''
    store.apiBaseUrl.value = apiBaseUrl

    if (name) {
      title.value = name
      store.storeName.value = name
    }
    if (favicon) {
      favicon.value = icon
    }

    // main.ts mounts without awaiting this, so route.path/query are still
    // START_LOCATION defaults until the initial navigation resolves.
    await router.isReady()

    if (route.query.login === 'true') {
      store.isLogin.value = true
      return
    }

    // Apply the cookie's route only from the default landing; pushing
    // unconditionally overrode explicit routes and stripped their query.
    const landedOnDefault = route.path === '/' || route.path === '/orders'
    if (path && landedOnDefault && path !== route.path) {
      router.push(path)
    }
    if (tokenFromStorage) {
      store.token.value = tokenFromStorage
      // The storefront opens this login in a tab of its own, so a customer who is
      // already signed in would otherwise land on an email form over a live session.
      // Presence is enough, and validity is not worth a round trip on every visit:
      // nothing in this app routes to /login, so there is no way back to loop through,
      // and a rejected token lands on /orders where the 401 handler renders the login
      // in place rather than navigating. `?login=true` returns above, which is what
      // still lets someone sign in as a different customer.
      if (route.path === '/login') {
        router.replace('/orders')
      }
    }
    if (userFromStorage) {
      store.user.value = userFromStorage
    }
  })
</script>

<template>
  <div id="app">
    <UIContentWrap>
      <div class="container">
        <div v-if="is404" class="flex items-center justify-center min-h-screen">
          <h1 class="text-8xl">{{ t('app.notFound') }}</h1>
        </div>
        <div v-else>
          <NavMenu v-if="!store.isLogin.value && route.path !== '/login'" />
          <RouterView v-if="!store.isLogin.value" />
          <LoginScreen v-else />
        </div>
      </div>
      <Notification />
    </UIContentWrap>
  </div>
</template>
